import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  type AddSnackbarActionAlertProps,
  type AddSnackbarActionAlertType,
  type AddSnackbarAlertType,
  type SeverityType,
  type SnackbarActionAlertType,
  type SnackbarHookType,
} from './types';

export const AUTO_HIDE_DURATION = 6000;
export const ANCHOR_ORIGIN = { horizontal: 'center', vertical: 'top' } as const;

export const useSnackbarAlert = (): SnackbarHookType => {
  const [snackbarAlert, setSnackbarAlert] = useState<SnackbarActionAlertType | null>(null);

  const unmountedRef = useRef(false);

  useEffect(
    () => () => {
      unmountedRef.current = true;
    },
    [],
  );

  const addSnackbarActionAlert: AddSnackbarActionAlertType = useCallback(
    ({
      actionFunction = () => {
        // use `autoHideDuration: null` for no auto hide
        // autoHideDuration is automatically set to `null` if `actionMessage` is defined
        /* do nothing */
      },
      actionMessage,
      anchorOrigin = ANCHOR_ORIGIN,
      autoHideDuration = AUTO_HIDE_DURATION,
      handleClose = () => {
        /* do nothing */
      },
      message,
      severity = 'info',
    }: AddSnackbarActionAlertProps) => {
      const onClose = () => {
        handleClose();
        setSnackbarAlert(null);
      };
      setSnackbarAlert({
        actionFunction,
        actionMessage,
        anchorOrigin,
        autoHideDuration: actionMessage ? null : autoHideDuration,
        message,
        onClose,
        severity,
      });

      if (actionMessage || autoHideDuration === null || autoHideDuration === undefined) return;

      setTimeout(() => {
        if (unmountedRef.current) return;

        setSnackbarAlert(null);
      }, autoHideDuration);
    },
    [],
  );

  // An easy way to get a notification with minimal inputs
  const addSnackbarAlert: AddSnackbarAlertType = useCallback(
    (message: React.ReactNode, severity: SeverityType = 'info') => {
      addSnackbarActionAlert({
        autoHideDuration: AUTO_HIDE_DURATION,
        message,
        severity,
      });
    },
    [addSnackbarActionAlert],
  );

  return {
    addSnackbarActionAlert,
    addSnackbarAlert,
    snackbarAlert,
  };
};
